import { createContext, Dispatch, JSX, SetStateAction, useRef, useState } from "react";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { reportName } from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { BulkProjectTypeEnum, IncludedReports, TerminationsWizardContextSteps, BulkProviderTerminationMethod } from "@app/terminations/import/interfaces";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { BulkProviderChangeProjectTableData, InvalidNpiTableData, InvalidTinTableData, ResultsSummaryTableData, ReviewProjectsTableData } from "@app/shared/provider-change-wizards/interfaces";
import ProjectService from "@app/projects/projectService";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";

export type VolumeTerminationType = "import" | "ghost";

interface VolumeTerminationsProps {
  terminationType: VolumeTerminationType;
  projectType?: BulkProjectTypeEnum;
}

interface TerminationsWizardContextProps {
  projectService: ProjectService;
  providerTerminationService: ProviderTerminationService;
  pciDashboardService: PciDashboardService;

  bulkProviderTerminationId: number;
  bulkProviderTerminationMethod: BulkProviderTerminationMethod;
  bulkImportFormData: FormData;
  comment: string;
  eligibleSelectedProjectIds: string[];
  emailOnSuccess: boolean;
  filename: string;
  formatType: string;
  ghostCountsCalculated: boolean;
  includedReports: IncludedReports;
  invalidNpis: InvalidNpiTableData[];
  invalidTins: InvalidTinTableData[];
  isDistinctServicingProviderReport: boolean;
  projects: BulkProviderChangeProjectTableData[];
  resultsSummary: ResultsSummaryTableData[];
  reviewProjects: ReviewProjectsTableData[];
  page: TerminationsWizardContextSteps;
  projectType: BulkProjectTypeEnum;
  selectedLineOfBusiness: string;
  /**
   * Persists here, rather than as a ref local to DistanceMeasurementRadioGroup, because that
   * component unmounts/remounts on Back/Next between wizard steps while this value's "session" is
   * the whole wizard — a component-local ref would forget the default was already applied and
   * silently re-apply it, clobbering a choice the user already made on revisiting Configure Report.
   */
  hasAppliedTimeDistanceRecalcDefault: boolean;
  shouldSkipTerminationTimeDistanceRecalc: boolean;
  terminateByAllLocations: boolean;
  terminateByAllSpecialties: boolean;
  terminateByNpi: boolean;
  terminateByTin: boolean;
  terminationReason: string;
  terminationType: VolumeTerminationType;
  // The ability to select projects by tag means that disabled projects can be selected indirectly.
  // We need to keep track of the total selected projects to make the tags UI behave as expected 
  // (e.g. clicking tag checkbox where the tag includes disabled projects still checks the box),
  // and also the eligible selections for validation and sending with the API request
  totalSelectedProjectIds: string[];

  setBulkProviderTerminationId: (id: number) => void;
  setBulkProviderTerminationMethod: (termintionMethod: BulkProviderTerminationMethod) => void;
  setBulkImportFormData: (formData: FormData) => void;
  setComment: (newComment: string) => void;
  setEligibleSelectedProjectIds: Dispatch<SetStateAction<string[]>>;
  setEmailOnSuccess: (flag: boolean) => void;
  setFormatType: (newType: string) => void;
  setFilename: (newFilename: string) => void;
  setGhostCountsCalculated: (isCalculated: boolean) => void;
  setHasAppliedTimeDistanceRecalcDefault: (newValue: boolean) => void;
  setInvalidNpis: (invalidNpis: InvalidNpiTableData[]) => void;
  setInvalidTins: (invalidTins: InvalidTinTableData[]) => void;
  setIsDistinctServicingProviderReport: (isDistinct: boolean) => void;
  setPage: (newPage: TerminationsWizardContextSteps) => void;
  setProjects: (projects: BulkProviderChangeProjectTableData[]) => void;
  setResultsSummary: (results: ResultsSummaryTableData[]) => void;
  setReviewProjects: (projects: ReviewProjectsTableData[]) => void;
  setSelectedLineOfBusiness: (lineOfBusiness: string) => void;
  setShouldSkipTerminationTimeDistanceRecalc: (newValue: boolean) => void;
  setTotalSelectedProjectIds: Dispatch<SetStateAction<string[]>>;
  setTerminateByAllLocations: (flag: boolean) => void;
  setTerminateByAllSpecialties: (flag: boolean) => void;
  setTerminateByNpi: (flag: boolean) => void;
  setTerminateByTin: (flag: boolean) => void;
  setTerminationReason: (newReason: string) => void;
  updateIncludedReports: (reportType: keyof IncludedReports, newValue: boolean) => void;

  reportPresets: Partial<Record<ReportType, ColumnConfigurationPresetOption[]>>;
  selectedReportPreset: Partial<Record<keyof IncludedReports, ColumnConfigurationPresetOption>>;
  setReportPresets: (reportType: ReportType, presets: ColumnConfigurationPresetOption[]) => void;
  setAllReportPresets: (presets: Partial<Record<ReportType, ColumnConfigurationPresetOption[]>>) => void;
  setSelectedReportPreset: (reportKey: keyof IncludedReports, preset: ColumnConfigurationPresetOption) => void;
  setAllSelectedReportPresets: (presets: Partial<Record<keyof IncludedReports, ColumnConfigurationPresetOption>>) => void;
}

const TerminationsWizardContext = createContext<Partial<TerminationsWizardContextProps>>({
  /* oxlint-disable sort-keys -- these are separated by global services, getters, and setters */
  projectService: null,
  providerTerminationService: null,
  pciDashboardService: null,

  bulkImportFormData: null,
  bulkProviderTerminationId: null,
  bulkProviderTerminationMethod: null,
  comment: "",
  emailOnSuccess: true,
  filename: "",
  formatType: "CSV",
  ghostCountsCalculated: false,
  hasAppliedTimeDistanceRecalcDefault: false,
  includedReports: null,
  invalidNpis: [],
  invalidTins: [],
  isDistinctServicingProviderReport: false,
  page: TerminationsWizardContextSteps.SelectProjects,
  projectType: BulkProjectTypeEnum.County,
  projects: [],
  resultsSummary: [],
  reviewProjects: [],
  selectedLineOfBusiness: "Total",
  shouldSkipTerminationTimeDistanceRecalc: false,
  totalSelectedProjectIds: [],
  terminateByAllLocations: true,
  terminateByAllSpecialties: true,
  terminateByNpi: true,
  terminateByTin: false,
  terminationReason: "",

  setBulkImportFormData: () => null,
  setBulkProviderTerminationId: () => null,
  setBulkProviderTerminationMethod: () => null,
  setComment: () => null,
  setEligibleSelectedProjectIds: () => null,
  setEmailOnSuccess: () => null,
  setFilename: () => null,
  setFormatType: () => null,
  setGhostCountsCalculated: () => null,
  setHasAppliedTimeDistanceRecalcDefault: () => null,
  setInvalidNpis: () => null,
  setInvalidTins: () => null,
  setIsDistinctServicingProviderReport: () => null,
  setPage: () => null,
  setProjects: () => null,
  setResultsSummary: () => null,
  setReviewProjects: () => null,
  setSelectedLineOfBusiness: () => null,
  setShouldSkipTerminationTimeDistanceRecalc: () => null,
  setTotalSelectedProjectIds: () => null,
  setTerminateByAllLocations: () => null,
  setTerminateByAllSpecialties: () => null,
  setTerminateByNpi: () => null,
  setTerminateByTin: () => null,
  setTerminationReason: () => null,
  updateIncludedReports: () => null,

  reportPresets: {},
  selectedReportPreset: {},
  setReportPresets: () => null,
  setAllReportPresets: () => null,
  setSelectedReportPreset: () => null,
  setAllSelectedReportPresets: () => null,
});

const TerminationsWizardProvider = (props: VolumeTerminationsProps & { children: React.ReactNode }): JSX.Element => {
  const [shouldSkipTerminationTimeDistanceRecalc, setShouldSkipTerminationTimeDistanceRecalc] = useState(false);
  const [hasAppliedTimeDistanceRecalcDefault, setHasAppliedTimeDistanceRecalcDefault] = useState(false);
  const { projectType = BulkProjectTypeEnum.County } = props;
  const projectService = useRef(new ProjectService()).current;
  const providerTerminationService = useRef(new ProviderTerminationService()).current;
  const pciDashboardService = useRef(new PciDashboardService()).current;

  const [bulkImportFormData, setBulkImportFormData] = useState<FormData>(null);
  const [bulkProviderTerminationId, setBulkProviderTerminationId] = useState<number>(null);
  const [comment, setComment] = useState("");
  const [eligibleSelectedProjectIds, setEligibleSelectedProjectIds] = useState<string[]>([]);
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [filename, setFilename] = useState(reportName(props.terminationType == "ghost" ? "GhostVolumeTerminations" : "VolumeTerminations"));
  const [formatType, setFormatType] = useState("CSV");
  const [ghostCountsCalculated, setGhostCountsCalculated] = useState(false);
  const [includedReports, setIncludedReports] = useState<IncludedReports>({
    projectDetailsTerminationOverview: true,
    serviceAreaSpecialtySummaryChangedGaps: true,
    serviceAreaSpecialtySummaryComparison: true,
    // Opportunity reports aren't a supported output for zip volume terminations - their checkboxes
    // are hidden in TerminationsWizardConfigureReport, so default them off rather than silently
    // submitting a report the user was never offered.
    opportunitySpecialtySummaryChangedGaps: projectType !== BulkProjectTypeEnum.Zip,
    opportunitySpecialtySummaryComparison: projectType !== BulkProjectTypeEnum.Zip,
    terminationUploadFile: !(props.terminationType == "ghost") ? true : false,
    serviceAreaProviders: false,
    ghostTerminationList: props.terminationType == "ghost" ? true : false,
    opportunityProviders: false,
    includeReportMetadataSummary: false
  });
  const [invalidNpis, setInvalidNpis] = useState<InvalidNpiTableData[]>([]);
  const [invalidTins, setInvalidTins] = useState<InvalidTinTableData[]>([]);
  const [isDistinctServicingProviderReport, setIsDistinctServicingProviderReport] = useState<boolean>(false);
  const [bulkProviderTerminationMethod, setBulkProviderTerminationMethod] = useState<BulkProviderTerminationMethod>(props.terminationType == "ghost" ? BulkProviderTerminationMethod.Ghost : BulkProviderTerminationMethod.Import);
  const [page, setPage] = useState<TerminationsWizardContextSteps>(TerminationsWizardContextSteps.SelectProjects);
  const [projects, setProjects] = useState<BulkProviderChangeProjectTableData[]>([]);
  const [resultsSummary, setResultsSummary] = useState<ResultsSummaryTableData[]>([]);
  const [reviewProjects, setReviewProjects] = useState<ReviewProjectsTableData[]>([]);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState("Total");
  const [totalSelectedProjectIds, setTotalSelectedProjectIds] = useState<string[]>([]);
  const [terminateByAllLocations, setTerminateByAllLocations] = useState(true);
  const [terminateByAllSpecialties, setTerminateByAllSpecialties] = useState(true);
  const [terminateByNpi, setTerminateByNpi] = useState(true);
  const [terminateByTin, setTerminateByTin] = useState(false);
  const [terminationReason, setTerminationReason] = useState("");
  // reportPresets are keyed on ReportType, because multiple reports share a report type and we want them to share presets
  const [reportPresets, setReportPresetsState] = useState<Partial<Record<ReportType, ColumnConfigurationPresetOption[]>>>({});
  // but we need to key them on something else for the selected value, so we'll use the key variables defined in the column config step
  const [selectedReportPreset, setSelectedReportPresetState] = useState<Partial<Record<keyof IncludedReports, ColumnConfigurationPresetOption>>>({});

  const setReportPresets = (reportType: ReportType, presets: ColumnConfigurationPresetOption[]) => {
    setReportPresetsState((prev) => ({ ...prev, [reportType]: presets }));
  };

  const setAllReportPresets = (presets: Partial<Record<ReportType, ColumnConfigurationPresetOption[]>>) => {
    setReportPresetsState((prev) => ({ ...prev, ...presets }));
  };

  const setSelectedReportPreset = (reportType: string, preset: ColumnConfigurationPresetOption) => {
    setSelectedReportPresetState((prev) => ({ ...prev, [reportType]: preset }));
  };

  const setAllSelectedReportPresets = (presets: Partial<Record<string, ColumnConfigurationPresetOption>>) => {
    setSelectedReportPresetState((prev) => ({ ...prev, ...presets }));
  };

  const updateIncludedReports = (changedReport: keyof IncludedReports, newValue: boolean) => {
    setIncludedReports((currentIncludedReports) => ({
      ...currentIncludedReports,
      [changedReport]: newValue,
    }))
  }

  const value = {
    projectService,
    providerTerminationService,
    pciDashboardService,

    bulkImportFormData,
    bulkProviderTerminationId,
    bulkProviderTerminationMethod,
    comment,
    eligibleSelectedProjectIds,
    emailOnSuccess,
    filename,
    formatType,
    ghostCountsCalculated,
    hasAppliedTimeDistanceRecalcDefault,
    includedReports,
    invalidNpis,
    invalidTins,
    isDistinctServicingProviderReport,
    page,
    projectType,
    projects,
    resultsSummary,
    reviewProjects,
    selectedLineOfBusiness,
    shouldSkipTerminationTimeDistanceRecalc,
    terminateByAllLocations,
    terminateByAllSpecialties,
    terminateByNpi,
    terminateByTin,
    terminationReason,
    terminationType: props.terminationType,
    totalSelectedProjectIds,

    setBulkImportFormData,
    setBulkProviderTerminationId,
    setBulkProviderTerminationMethod,
    setComment,
    setEligibleSelectedProjectIds,
    setEmailOnSuccess,
    setFilename,
    setFormatType,
    setGhostCountsCalculated,
    setHasAppliedTimeDistanceRecalcDefault,
    setInvalidNpis,
    setInvalidTins,
    setIsDistinctServicingProviderReport,
    setPage,
    setProjects,
    setResultsSummary,
    setReviewProjects,
    setSelectedLineOfBusiness,
    setShouldSkipTerminationTimeDistanceRecalc,
    setTerminateByAllLocations,
    setTerminateByAllSpecialties,
    setTerminateByNpi,
    setTerminateByTin,
    setTerminationReason,
    setTotalSelectedProjectIds,
    updateIncludedReports,

    reportPresets,
    selectedReportPreset,
    setReportPresets,
    setAllReportPresets,
    setSelectedReportPreset,
    setAllSelectedReportPresets,
  };
  /* oxlint-enable sort-keys */

  return (
    <TerminationsWizardContext.Provider value={value}>
      {props.children}
    </TerminationsWizardContext.Provider>
  );

};

export { TerminationsWizardContext, TerminationsWizardProvider };
